// ============================================================================
// BULK ROOM EDIT MODAL
// ============================================================================
// Add or edit many rooms for a single (Property, Room Category) at once.
// Replaces the one-room-at-a-time RoomModal flow when an operator is setting
// up rooms in bulk (e.g. all 30 rooms of a "Deluxe Twin" category on opening).
//
// Flow:
//   1. Pick Property + Room Category at top.
//   2. Existing rooms for that combination are loaded into the editable grid.
//      (Rooms outside the chosen category are not shown.)
//   3. Operator can edit any row, add new rows, mark rows for deletion, or
//      use the "Generate Sequence" helper to fill room numbers (e.g. 201–220
//      with floor=2). Each row tracks its own _state so we know which rows to
//      create / update / delete on Save.
//   4. Save processes each row sequentially against roomService.create /
//      .update / .delete and reports per-row results in a summary toast.
//      Successful rows leave the grid; failing rows stay with an error message.

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { roomService } from 'api/services/roomService';
import { propertyService } from 'api/services/propertyService';
import { roomCategoryService } from 'api/services/roomCategoryService';
import type { Room } from 'api/types/room.types';

// ============================================================================
// TYPES
// ============================================================================

type RowState = 'existing' | 'new' | 'modified' | 'deleted';

interface BulkRoomRow {
  /** Local row id — uuid-ish for new rows, real DB id for existing. */
  rowKey: string;
  /** DB id (only set when this row maps to an existing room). */
  roomId?: string;
  rowVersion?: number;
  roomNumber: string;
  floorNumber: string;
  roomStatus: string;
  isActive: boolean;
  state: RowState;
  /** Per-row error from the last save attempt; cleared on edit. */
  error?: string;
}

interface BulkRoomEditModalProps {
  isOpen: boolean;
  /** Optional pre-selected property + category. */
  initialPropertyId?: string;
  initialRoomCategoryId?: string;
  onSaved: () => void;
  onClose: () => void;
  // --- Minimize support ---
  /** Required for minimize support — identifies which tab the parked modal belongs to. */
  tabId?: string;
  moduleName?: string;
  /** Parent calls this when restoring a minimized session, so the parent can re-open the modal. */
  onRestoreModal?: () => void;
  /** Parent ref the modal assigns its `handleMinimize` to (allows auto-minimize from parent). */
  minimizeRef?: React.MutableRefObject<(() => void) | null>;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const ROOM_STATUS_OPTIONS: SelectOption[] = [
  { value: 'Available', label: 'Available' },
  { value: 'Occupied',  label: 'Occupied' },
  { value: 'Dirty',     label: 'Dirty' },
  { value: 'OOO',       label: 'Out of Order' },
  { value: 'Blocked',   label: 'Blocked' },
];

const EMPTY_ROW = (): BulkRoomRow => ({
  rowKey: `new-${Math.random().toString(36).slice(2, 11)}`,
  roomNumber: '',
  floorNumber: '',
  roomStatus: 'Available',
  isActive: true,
  state: 'new',
});

// ============================================================================
// COMPONENT
// ============================================================================

const BulkRoomEditModal: React.FC<BulkRoomEditModalProps> = ({
  isOpen,
  initialPropertyId,
  initialRoomCategoryId,
  onSaved,
  onClose,
  tabId,
  moduleName,
  onRestoreModal,
  minimizeRef,
}) => {
  // --- Minimize plumbing (parks the modal so cashier can navigate elsewhere) ---
  const { minimize } = useModalMinimize();

  // --- Selection state ---
  const [propertyId, setPropertyId] = useState(initialPropertyId || '');
  const [categoryId, setCategoryId] = useState(initialRoomCategoryId || '');

  // --- Rows ---
  const [rows, setRows] = useState<BulkRoomRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // --- Refs that always carry the latest state (read at minimize/restore time
  //     to avoid stale-closure capture). ---
  const propertyIdRef = useRef(propertyId);  propertyIdRef.current = propertyId;
  const categoryIdRef = useRef(categoryId);  categoryIdRef.current = categoryId;
  const rowsRef = useRef(rows);              rowsRef.current = rows;
  /** When set, the next isOpen=true effect skips the reset/reload — used by
   *  the restore path to preserve in-flight edits. */
  const isRestoreRef = useRef(false);

  // --- Generate-sequence helper state ---
  const [genStart, setGenStart] = useState('101');
  const [genCount, setGenCount] = useState('5');
  const [genFloor, setGenFloor] = useState('1');
  const [genPrefix, setGenPrefix] = useState('');

  // --- Discard confirmation ---
  const [discardOpen, setDiscardOpen] = useState(false);
  const [confirmContextSwitchOpen, setConfirmContextSwitchOpen] = useState<{ kind: 'property' | 'category'; nextId: string } | null>(null);

  // --- Async option loaders ---
  const loadPropertyOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: input || undefined, pageSize: input ? 50 : 500, includeInactive: false });
    return res.data.map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
  }, []);
  const loadCategoryOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    const res = await roomCategoryService.getAll({
      searchTerm: input || undefined,
      propertyId: propertyId || undefined,
      pageSize: input ? 50 : 500,
      includeInactive: false,
    });
    return res.data.map((c: any) => ({ value: c.id, label: `${c.categoryCode} - ${c.categoryName}` }));
  }, [propertyId]);

  // --- Load existing rooms for the chosen property + category ---
  const loadExistingRooms = useCallback(async () => {
    if (!propertyId || !categoryId) {
      setRows([EMPTY_ROW(), EMPTY_ROW(), EMPTY_ROW(), EMPTY_ROW(), EMPTY_ROW()]);
      return;
    }
    setLoading(true);
    try {
      const res = await roomService.getAll({
        propertyId, roomCategoryId: categoryId, pageSize: 200, includeInactive: true,
      });
      const existing: BulkRoomRow[] = (res.data as Room[]).map(r => ({
        rowKey: r.id,
        roomId: r.id,
        rowVersion: r.rowVersion,
        roomNumber: r.roomNumber,
        floorNumber: r.floorNumber || '',
        roomStatus: r.roomStatus || 'Available',
        isActive: r.isActive,
        state: 'existing',
      }));
      // Always append a few blank rows to invite adding more.
      setRows([...existing, EMPTY_ROW(), EMPTY_ROW(), EMPTY_ROW()]);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Failed to load existing rooms.');
      setRows([EMPTY_ROW(), EMPTY_ROW(), EMPTY_ROW(), EMPTY_ROW(), EMPTY_ROW()]);
    } finally {
      setLoading(false);
    }
  }, [propertyId, categoryId]);

  // Re-load when the modal opens or the (property, category) pair changes.
  // Restore path: skip the reload — the rows we're restoring are already in state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) return; // restore path consumes the flag in the reset effect below
    loadExistingRooms();
  }, [isOpen, propertyId, categoryId]);

  // Reset selections when the modal is opened fresh.
  // Restore path: clear the flag and skip the reset so saved selections + rows survive.
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; return; }
    setPropertyId(initialPropertyId || '');
    setCategoryId(initialRoomCategoryId || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // --- Dirty tracking ---
  const isDirty = useMemo(() => rows.some(r => r.state === 'new' && (r.roomNumber || r.floorNumber !== '' || r.roomStatus !== 'Available') ||
    r.state === 'modified' || r.state === 'deleted'), [rows]);

  // --- Row mutators ---
  const updateRow = useCallback((rowKey: string, patch: Partial<BulkRoomRow>) => {
    setRows(prev => prev.map(r => {
      if (r.rowKey !== rowKey) return r;
      const next = { ...r, ...patch, error: undefined };
      // Existing rows promote to 'modified' on any field edit.
      if (r.state === 'existing') next.state = 'modified';
      return next;
    }));
  }, []);

  const addRow = useCallback(() => {
    setRows(prev => [...prev, EMPTY_ROW()]);
  }, []);

  const removeRow = useCallback((rowKey: string) => {
    setRows(prev => prev.flatMap(r => {
      if (r.rowKey !== rowKey) return [r];
      // New row → drop entirely. Existing/modified row → mark for delete.
      if (r.state === 'new') return [];
      return [{ ...r, state: 'deleted' as RowState, error: undefined }];
    }));
  }, []);

  const undeleteRow = useCallback((rowKey: string) => {
    setRows(prev => prev.map(r => r.rowKey === rowKey ? { ...r, state: r.roomId ? 'existing' : 'new' } : r));
  }, []);

  // --- Generate-sequence helper ---
  const handleGenerate = useCallback(() => {
    const start = parseInt(genStart, 10);
    const count = parseInt(genCount, 10);
    if (Number.isNaN(start) || Number.isNaN(count) || count <= 0 || count > 100) {
      notificationService.toast.error('Provide a numeric start and a count between 1 and 100.');
      return;
    }
    const newRows: BulkRoomRow[] = [];
    for (let i = 0; i < count; i++) {
      newRows.push({
        ...EMPTY_ROW(),
        roomNumber: `${genPrefix}${start + i}`,
        floorNumber: genFloor,
      });
    }
    // Append to the end of the current list (preserves any existing rows the
    // operator may have already started editing).
    setRows(prev => {
      // Drop any trailing fully-empty new rows so the generated rooms slot in
      // immediately after real content.
      const trailingEmpties = (() => {
        let n = 0;
        for (let i = prev.length - 1; i >= 0; i--) {
          const r = prev[i];
          if (r.state === 'new' && !r.roomNumber && !r.floorNumber) n++;
          else break;
        }
        return n;
      })();
      const trimmed = trailingEmpties > 0 ? prev.slice(0, prev.length - trailingEmpties) : prev;
      return [...trimmed, ...newRows];
    });
    notificationService.toast.success(`Generated ${count} room number(s).`);
  }, [genStart, genCount, genFloor, genPrefix]);

  // --- Save ---
  const handleSave = useCallback(async () => {
    if (!propertyId || !categoryId) {
      notificationService.toast.error('Pick a property and a room category before saving.');
      return;
    }
    // Validate locally first: every non-deleted row must have a non-empty room number.
    const toProcess = rows.filter(r => r.state !== 'existing');
    const validatable = toProcess.filter(r => r.state !== 'deleted');
    const blanks = validatable.filter(r => !r.roomNumber.trim());
    // Ignore fully-blank "new" rows (operator added an extra row but didn't fill it) —
    // skip them silently rather than erroring.
    const meaningful = validatable.filter(r => r.state !== 'new' || r.roomNumber.trim() || r.floorNumber.trim());
    if (meaningful.length === 0 && toProcess.filter(r => r.state === 'deleted').length === 0) {
      notificationService.toast.warning('No changes to save.');
      return;
    }
    if (blanks.some(r => r.state !== 'new' || r.floorNumber.trim())) {
      notificationService.toast.error('Room number is required on every row.');
      return;
    }
    // Duplicate-room-number guard within the local set.
    const numbers = meaningful.map(r => r.roomNumber.trim().toLowerCase());
    const dupes = numbers.filter((n, i) => numbers.indexOf(n) !== i);
    if (dupes.length > 0) {
      notificationService.toast.error(`Duplicate room number in batch: ${[...new Set(dupes)].join(', ')}`);
      return;
    }

    setSaving(true);
    let successCount = 0;
    let failCount = 0;
    const updatedRows: BulkRoomRow[] = [...rows];
    for (let i = 0; i < updatedRows.length; i++) {
      const r = updatedRows[i];
      // Skip existing-unchanged rows entirely.
      if (r.state === 'existing') continue;
      // Skip fully-empty new rows (operator left blanks at the end).
      if (r.state === 'new' && !r.roomNumber.trim() && !r.floorNumber.trim()) continue;
      try {
        if (r.state === 'deleted' && r.roomId && r.rowVersion != null) {
          await roomService.delete(r.roomId, r.rowVersion);
          updatedRows[i] = { ...r, error: undefined };
        } else if (r.state === 'modified' && r.roomId && r.rowVersion != null) {
          const res = await roomService.update(r.roomId, {
            propertyId, roomCategoryId: categoryId,
            roomNumber: r.roomNumber.trim(),
            floorNumber: r.floorNumber.trim() || undefined,
            roomStatus: r.roomStatus || undefined,
            isActive: r.isActive,
            rowVersion: r.rowVersion,
          });
          updatedRows[i] = { ...r, state: 'existing', error: undefined, rowVersion: (res.data?.rowVersion ?? r.rowVersion) };
        } else if (r.state === 'new') {
          const res = await roomService.create({
            propertyId, roomCategoryId: categoryId,
            roomNumber: r.roomNumber.trim(),
            floorNumber: r.floorNumber.trim() || undefined,
            roomStatus: r.roomStatus || undefined,
            isActive: r.isActive,
          });
          updatedRows[i] = {
            ...r,
            state: 'existing',
            roomId: res.data?.id,
            rowVersion: res.data?.rowVersion,
            error: undefined,
          };
        }
        successCount++;
      } catch (err: any) {
        failCount++;
        const msg = err?.response?.data?.message || err?.message || 'Save failed';
        updatedRows[i] = { ...r, error: msg };
      }
    }
    // Drop deleted rows that succeeded; keep the rest (including failures with their error messages).
    const finalRows = updatedRows.filter(r => !(r.state === 'deleted' && !r.error));
    setRows(finalRows);
    setSaving(false);

    if (failCount === 0) {
      notificationService.toast.success(`Saved ${successCount} room change(s).`);
      onSaved();
      // Don't auto-close — let the operator add more or close manually.
    } else {
      notificationService.toast.error(`${successCount} saved · ${failCount} failed. Review the highlighted rows.`);
      // Even with partial failures, refresh upstream grid for the rows that did succeed.
      if (successCount > 0) onSaved();
    }
  }, [rows, propertyId, categoryId, onSaved]);

  const handleClose = useCallback(() => {
    if (isDirty && !saving) {
      setDiscardOpen(true);
      return;
    }
    onClose();
  }, [isDirty, saving, onClose]);

  // --- Minimize: park the modal so the cashier can navigate elsewhere. ---
  // Capture the current state in closures (NOT via deps) so a re-open of this
  // same modal slot for a different session doesn't clobber the saved data.
  const handleMinimize = useCallback(() => {
    if (!tabId) return;
    const savedRows = rowsRef.current.map(r => ({ ...r }));
    const savedPropertyId = propertyIdRef.current;
    const savedCategoryId = categoryIdRef.current;
    const dirtyAtMinimize = savedRows.some(r =>
      (r.state === 'new' && (r.roomNumber || r.floorNumber !== '' || r.roomStatus !== 'Available')) ||
      r.state === 'modified' || r.state === 'deleted'
    );
    const rowCount = savedRows.filter(r => r.state !== 'deleted').length;
    const titleSuffix = savedPropertyId && savedCategoryId
      ? ` (${rowCount} row${rowCount === 1 ? '' : 's'})`
      : '';
    minimize({
      id: `${tabId}-bulk-room-modal`,
      title: `Bulk Add / Edit Rooms${titleSuffix}`,
      moduleName: moduleName || 'Room Master',
      tabId,
      isDirty: dirtyAtMinimize,
      onRestore: () => {
        isRestoreRef.current = true;
        setPropertyId(savedPropertyId);
        setCategoryId(savedCategoryId);
        setRows(savedRows);
        if (onRestoreModal) onRestoreModal();
      },
    });
    onClose();
  }, [tabId, moduleName, onRestoreModal, minimize, onClose]);

  // Expose the handler so the parent screen can auto-minimize this modal.
  if (minimizeRef) minimizeRef.current = handleMinimize;

  // Property / category changes mid-edit prompt for confirm if there's pending work.
  const handlePropertyChange = useCallback((nextId: string) => {
    if (isDirty && nextId !== propertyId) {
      setConfirmContextSwitchOpen({ kind: 'property', nextId });
    } else {
      setPropertyId(nextId);
      setCategoryId('');
    }
  }, [isDirty, propertyId]);
  const handleCategoryChange = useCallback((nextId: string) => {
    if (isDirty && nextId !== categoryId) {
      setConfirmContextSwitchOpen({ kind: 'category', nextId });
    } else {
      setCategoryId(nextId);
    }
  }, [isDirty, categoryId]);
  const confirmContextSwitch = useCallback(() => {
    if (!confirmContextSwitchOpen) return;
    if (confirmContextSwitchOpen.kind === 'property') {
      setPropertyId(confirmContextSwitchOpen.nextId);
      setCategoryId('');
    } else {
      setCategoryId(confirmContextSwitchOpen.nextId);
    }
    setConfirmContextSwitchOpen(null);
  }, [confirmContextSwitchOpen]);

  // --- Display partitioning: deleted rows render at bottom in muted style. ---
  // NOTE: keep all hooks above the conditional return below — React requires a
  // stable hook order between renders, and an early `if (!isOpen) return null`
  // before a useMemo would change the hook count when the modal opens.
  const liveRows = useMemo(() => rows.filter(r => r.state !== 'deleted'), [rows]);
  const deletedRows = useMemo(() => rows.filter(r => r.state === 'deleted'), [rows]);
  const summary = useMemo(() => {
    const created = rows.filter(r => r.state === 'new' && (r.roomNumber.trim() || r.floorNumber.trim())).length;
    const modified = rows.filter(r => r.state === 'modified').length;
    const deleted = rows.filter(r => r.state === 'deleted').length;
    return { created, modified, deleted };
  }, [rows]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={() => !saving && handleClose()} />
      <div className="modal-container modal-xl">
        {saving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving rooms...</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Bulk Add / Edit Rooms</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            {tabId && (
              <button
                className="modal-header-btn"
                onClick={handleMinimize}
                disabled={saving}
                title="Minimize to bar"
              >
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={saving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxHeight: '70vh' }}>
          {/* Property + Category selectors */}
          <div className="form-grid-2">
            <SyutSelect
              label="Property"
              required
              isAsync
              loadOptions={loadPropertyOptions}
              defaultOptions
              cacheOptions={false}
              value={propertyId}
              onChange={(val) => handlePropertyChange((val as string) || '')}
              placeholder="Select property..."
              isClearable={false}
              size="sm"
            />
            <SyutSelect
              key={propertyId || 'no-prop'}
              label="Room Category"
              required
              isAsync
              loadOptions={loadCategoryOptions}
              defaultOptions
              cacheOptions={false}
              value={categoryId}
              onChange={(val) => handleCategoryChange((val as string) || '')}
              placeholder={propertyId ? 'Select category...' : 'Pick a property first'}
              isDisabled={!propertyId}
              isClearable={false}
              size="sm"
            />
          </div>

          {/* Generate-sequence helper */}
          <div style={{
            border: '1px solid var(--border-color)', borderRadius: '0.5rem',
            padding: '0.625rem 0.875rem', background: 'var(--bg-secondary)',
            display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '0.5rem',
          }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>GENERATE</span>
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 80 }}>
              <label style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Prefix</label>
              <input type="text" className="form-input" value={genPrefix} onChange={(e) => setGenPrefix(e.target.value)}
                placeholder="(none)" style={{ fontSize: '0.8rem', padding: '0.3rem 0.5rem' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 80 }}>
              <label style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Start #</label>
              <input type="number" className="form-input" value={genStart} onChange={(e) => setGenStart(e.target.value)}
                style={{ fontSize: '0.8rem', padding: '0.3rem 0.5rem' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 80 }}>
              <label style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Count</label>
              <input type="number" className="form-input" value={genCount} onChange={(e) => setGenCount(e.target.value)}
                min={1} max={100} style={{ fontSize: '0.8rem', padding: '0.3rem 0.5rem' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 80 }}>
              <label style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Floor</label>
              <input type="text" className="form-input" value={genFloor} onChange={(e) => setGenFloor(e.target.value)}
                style={{ fontSize: '0.8rem', padding: '0.3rem 0.5rem' }} />
            </div>
            <button className="btn btn-secondary" onClick={handleGenerate} disabled={!categoryId}
              style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem' }}>
              <IconSystem.add size={14} /> Generate Rows
            </button>
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>
              e.g. prefix "A-" + start 101 + count 10 + floor "1" → A-101…A-110 on floor 1
            </span>
          </div>

          {/* Editable rows table */}
          <div style={{ flex: 1, minHeight: 240, overflow: 'auto', border: '1px solid var(--border-color)', borderRadius: '0.5rem' }}>
            {loading && <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>Loading existing rooms…</div>}
            {!loading && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead style={{ background: 'var(--bg-secondary)', position: 'sticky', top: 0 }}>
                  <tr>
                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left', borderBottom: '1px solid var(--border-color)', width: 32 }}></th>
                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left', borderBottom: '1px solid var(--border-color)', minWidth: 120 }}>Room # *</th>
                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left', borderBottom: '1px solid var(--border-color)', minWidth: 100 }}>Floor</th>
                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left', borderBottom: '1px solid var(--border-color)', minWidth: 140 }}>Status</th>
                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'center', borderBottom: '1px solid var(--border-color)', width: 72 }}>Active</th>
                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left', borderBottom: '1px solid var(--border-color)', width: 100 }}>State</th>
                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'center', borderBottom: '1px solid var(--border-color)', width: 48 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {liveRows.map(r => (
                    <tr key={r.rowKey} style={{
                      borderBottom: '1px solid var(--border-color)',
                      background: r.error ? 'rgba(254, 226, 226, 0.4)' : undefined,
                    }}>
                      <td style={{ padding: '0.25rem 0.5rem', color: 'var(--text-muted)' }}>
                        {r.state === 'new' ? <IconSystem.add size={12} /> :
                         r.state === 'modified' ? <IconSystem.edit size={12} /> :
                         <IconSystem.check size={12} />}
                      </td>
                      <td style={{ padding: '0.25rem 0.5rem' }}>
                        <input type="text" className="form-input"
                          value={r.roomNumber}
                          onChange={(e) => updateRow(r.rowKey, { roomNumber: e.target.value })}
                          placeholder="Room number"
                          style={{ fontSize: '0.8rem', padding: '0.3rem 0.5rem' }}
                        />
                        {r.error && <div style={{ fontSize: '0.7rem', color: '#991b1b', marginTop: '0.125rem' }}>{r.error}</div>}
                      </td>
                      <td style={{ padding: '0.25rem 0.5rem' }}>
                        <input type="text" className="form-input"
                          value={r.floorNumber}
                          onChange={(e) => updateRow(r.rowKey, { floorNumber: e.target.value })}
                          placeholder="Floor"
                          style={{ fontSize: '0.8rem', padding: '0.3rem 0.5rem' }}
                        />
                      </td>
                      <td style={{ padding: '0.25rem 0.5rem' }}>
                        <SyutSelect
                          options={ROOM_STATUS_OPTIONS}
                          value={r.roomStatus}
                          onChange={(val) => updateRow(r.rowKey, { roomStatus: (val as string) || 'Available' })}
                          isClearable={false}
                          size="sm"
                        />
                      </td>
                      <td style={{ padding: '0.25rem 0.5rem', textAlign: 'center' }}>
                        <input type="checkbox" checked={r.isActive}
                          onChange={(e) => updateRow(r.rowKey, { isActive: e.target.checked })}
                          style={{ width: 16, height: 16 }} />
                      </td>
                      <td style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem', fontWeight: 600 }}>
                        {r.state === 'new' && <span style={{ color: '#1e40af' }}>NEW</span>}
                        {r.state === 'modified' && <span style={{ color: '#92400e' }}>MODIFIED</span>}
                        {r.state === 'existing' && <span style={{ color: 'var(--text-muted)' }}>SAVED</span>}
                      </td>
                      <td style={{ padding: '0.25rem 0.5rem', textAlign: 'center' }}>
                        <button onClick={() => removeRow(r.rowKey)} title="Remove row"
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#991b1b', padding: '0.25rem' }}>
                          <IconSystem.trash size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {deletedRows.length > 0 && (
                    <tr><td colSpan={7} style={{ padding: '0.5rem', fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', background: 'var(--bg-secondary)' }}>
                      MARKED FOR DELETE — saved on next Save All
                    </td></tr>
                  )}
                  {deletedRows.map(r => (
                    <tr key={r.rowKey} style={{ borderBottom: '1px solid var(--border-color)', opacity: 0.55, textDecoration: 'line-through' }}>
                      <td style={{ padding: '0.25rem 0.5rem' }}><IconSystem.trash size={12} /></td>
                      <td style={{ padding: '0.25rem 0.5rem' }}>{r.roomNumber}</td>
                      <td style={{ padding: '0.25rem 0.5rem' }}>{r.floorNumber || '-'}</td>
                      <td style={{ padding: '0.25rem 0.5rem' }}>{r.roomStatus}</td>
                      <td style={{ padding: '0.25rem 0.5rem', textAlign: 'center' }}>{r.isActive ? '✓' : '—'}</td>
                      <td style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem', fontWeight: 600, color: '#991b1b' }}>DELETING</td>
                      <td style={{ padding: '0.25rem 0.5rem', textAlign: 'center' }}>
                        <button onClick={() => undeleteRow(r.rowKey)} title="Undo delete"
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--primary)', padding: '0.25rem' }}>
                          <IconSystem.refresh size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Footer info row + Add Row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            <button className="btn btn-secondary" onClick={addRow} disabled={!categoryId}
              style={{ fontSize: '0.8rem', padding: '0.3rem 0.7rem' }}>
              <IconSystem.add size={14} /> Add Row
            </button>
            <span>
              <strong style={{ color: 'var(--text-primary)' }}>{liveRows.length}</strong> visible row(s)
              {summary.created > 0 && <> · <span style={{ color: '#1e40af' }}>+{summary.created} new</span></>}
              {summary.modified > 0 && <> · <span style={{ color: '#92400e' }}>{summary.modified} modified</span></>}
              {summary.deleted > 0 && <> · <span style={{ color: '#991b1b' }}>−{summary.deleted} delete</span></>}
            </span>
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Close</button>
          <button className="btn btn-primary" onClick={handleSave}
            disabled={saving || !propertyId || !categoryId || !isDirty}>
            <IconSystem.save size={14} /> Save All
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={discardOpen}
        title="Discard Changes?"
        message="You have unsaved row edits. Discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardOpen(false); onClose(); }}
        onCancel={() => setDiscardOpen(false)}
      />

      <ConfirmDialog
        open={!!confirmContextSwitchOpen}
        title="Switch Context?"
        message={confirmContextSwitchOpen
          ? `Changing the ${confirmContextSwitchOpen.kind} will discard your unsaved row edits. Continue?`
          : ''}
        confirmLabel="Switch"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={confirmContextSwitch}
        onCancel={() => setConfirmContextSwitchOpen(null)}
      />
    </>
  );
};

export default BulkRoomEditModal;
